/**
 * Mount point for the visitor-events SDK (backend#411/#412, web#244).
 *
 * Renders nothing, ever. Its whole job is to decide — cheaply, on the entry
 * bundle's budget — whether the sender is worth loading at all, and then to
 * record one `view` per route change on the surfaces where per-visitor
 * analytics is in scope.
 *
 * ## Three gates, in this order, and the order matters
 *
 * 1. **Surface.** `isConsentSurface()` from `@/lib/consent` — the same
 *    predicate the consent banner uses, imported rather than re-listed.
 *    #244 moved that helper into the lib precisely so a second list could not
 *    drift away from the first. Clip pages, the spotlight gallery and bio
 *    pages; never the landing page, never the authed owner surfaces, never
 *    the legal pages.
 * 2. **Consent.** `visitorAnalyticsAllowed()` — an explicit stored grant, and
 *    nothing else. The SDK re-checks this itself before every queue and every
 *    request; the check here just avoids loading a chunk that would no-op.
 * 3. **Armed.** `visitorAnalyticsArmed()` — the env switch, off by default.
 *    See `@/lib/visitor-events/config.ts` for why (legal review of the
 *    Privacy Policy text is still outstanding).
 *
 * Only past all three does the dynamic `import()` happen. With the switch
 * unset — which is every deployment as of this commit — the sender's chunk is
 * never requested, no session id is minted, and no listener is attached. That
 * also keeps it out of the entry bundle, which matters here beyond byte count:
 * BK-199-4 fails the build if the landing page pulls chunks it should not, and
 * the public tree stays provider-free by construction.
 *
 * The import is fire-and-forget and its rejection is swallowed: an analytics
 * chunk that fails to load must not surface an error on a creator's clip page.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isConsentSurface, visitorAnalyticsAllowed } from "@/lib/consent";
import { visitorAnalyticsArmed } from "@/lib/visitor-events/config";

export default function VisitorAnalytics() {
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    if (!isConsentSurface(pathname)) return;
    if (!visitorAnalyticsAllowed()) return;
    if (!visitorAnalyticsArmed()) return;

    let cancelled = false;
    void import("@/lib/visitor-events/client")
      .then((sdk) => {
        if (cancelled) return;
        sdk.recordVisitorView({ pageId: sdk.visitorPageId(pathname), path: pathname });
      })
      .catch(() => {
        // Chunk failed to load. Nothing is tracked; nothing else changes.
      });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
}
